import { Injectable, inject } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { from } from 'rxjs'
import { map, switchMap, tap } from 'rxjs/operators'
import { ProductsService } from 'src/services/data/products.service'
import { ToasterService } from 'src/shared/toaster/toaster.service'
import { catchAndLog } from 'src/shared/utils/catch-and-log'
import { addProductHsCode, addProductHsCodeFailure, addProductHsCodeSuccess, archiveProduct, createProduct, createProductFailure, createProductSuccess, loadProducts, loadProductsFailure, loadProductsSuccess, removeProductHsCode, removeProductHsCodeFailure, removeProductHsCodeSuccess, unarchiveProduct, updateProduct, updateProductFailure, updateProductHsCode, updateProductHsCodeFailure, updateProductHsCodeSuccess, updateProductSuccess } from './product.actions'

@Injectable()
export class ProductEffects {
  private actions$ = inject(Actions)
  private Products = inject(ProductsService)
  private toaster = inject(ToasterService)


  loadProducts$ = createEffect(() => this.actions$.pipe(
    ofType(loadProducts),
    switchMap(() =>
      from(this.Products.getAll()).pipe(
        map(products => loadProductsSuccess({ products })),
        catchAndLog(this.toaster, 'Unable to fetch products', loadProductsFailure)))))

  createProduct$ = createEffect(() => this.actions$.pipe(
    ofType(createProduct),
    tap(() => this.toaster.progress('Creating product...')),
    switchMap(action =>
      from(this.Products.create(action.product)).pipe(
        map(product => createProductSuccess({ product })),
        tap(() => this.toaster.success('Product created successfully.')),
        catchAndLog(this.toaster, 'Unable to create product', createProductFailure)))))

  updateProduct$ = createEffect(() => this.actions$.pipe(
    ofType(updateProduct),
    tap(() => this.toaster.progress('Updating product...')),
    switchMap(action =>
      from(this.Products.update({ ...action.product, product_id: action.id })).pipe(
        map(product => updateProductSuccess({ product })),
        tap(() => this.toaster.success('Product updated successfully.')),
        catchAndLog(this.toaster, 'Unable to update product', updateProductFailure)))))

  archiveProduct$ = createEffect(() => this.actions$.pipe(
    ofType(archiveProduct),
    tap(() => this.toaster.progress('Archiving product...')),
    switchMap(action =>
      from(this.Products.update({ product_id: action.id, archived: 1 })).pipe(
        map(product => updateProductSuccess({ product })),
        tap(() => this.toaster.success('Product archived successfully.')),
        catchAndLog(this.toaster, 'Unable to update product', updateProductFailure)))))

  unarchiveProduct$ = createEffect(() => this.actions$.pipe(
    ofType(unarchiveProduct),
    tap(() => this.toaster.progress('Unarchiving product...')),
    switchMap(action =>
      from(this.Products.update({ product_id: action.id, archived: 0 })).pipe(
        map(product => updateProductSuccess({ product })),
        tap(() => this.toaster.success('Product unarchived successfully.')),
        catchAndLog(this.toaster, 'Unable to update product', updateProductFailure)))))

  addProductHsCode$ = createEffect(() => this.actions$.pipe(
    ofType(addProductHsCode),
    tap(() => this.toaster.progress('Adding HS code...')),
    switchMap(action =>
      from(this.Products.addHsCode(action.productId, action.data)).pipe(
        map(product => addProductHsCodeSuccess({ product })),
        tap(() => this.toaster.success('HS code added successfully.')),
        catchAndLog(this.toaster, 'Unable to add HS code', addProductHsCodeFailure)))))

  updateProductHsCode$ = createEffect(() => this.actions$.pipe(
    ofType(updateProductHsCode),
    tap(() => this.toaster.progress('Updating HS code...')),
    switchMap(action =>
      from(this.Products.updateHsCode(action.productId, action.data)).pipe(
        map(product => updateProductHsCodeSuccess({ product })),
        tap(() => this.toaster.success('HS code updated successfully.')),
        catchAndLog(this.toaster, 'Unable to update HS code', updateProductHsCodeFailure)))))

  removeProductHsCode$ = createEffect(() => this.actions$.pipe(
    ofType(removeProductHsCode),
    tap(() => this.toaster.progress('Removing HS code...')),
    switchMap(action =>
      from(this.Products.removeHsCode(action.productId, action.data)).pipe(
        map(product => removeProductHsCodeSuccess({ product })),
        tap(() => this.toaster.success('HS code removed successfully.')),
        catchAndLog(this.toaster, 'Unable to remove HS code', removeProductHsCodeFailure)))))
}
